import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? "http://localhost:8080",
  withCredentials: true,
});

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

axiosInstance.interceptors.request.use(config => {
  if (config.url !== "/auth/refresh") {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

async function handleUnauthorized(originalRequest: RetryableRequestConfig) {
  if (originalRequest._retry) {
    localStorage.removeItem("token");
    const apiUrl = (import.meta.env.VITE_API_URL || window.location.origin).replace(/\/+$/, "");
    window.location.href = `${apiUrl}/login/github`;
    return Promise.reject(new Error("Authentication failed"));
  }
  
  originalRequest._retry = true;
  try {
    const refreshResponse = await axiosInstance.get("/auth/refresh", {
      withCredentials: true,
    });
    const { token: newToken } = refreshResponse.data;
    if (!newToken) {
      throw new Error("No token in refresh response");
    }
    localStorage.setItem("token", newToken);
    originalRequest.headers.Authorization = `Bearer ${newToken}`;
    return axiosInstance(originalRequest);
  } catch (refreshError) {
    localStorage.removeItem("token");
    const apiUrl = (import.meta.env.VITE_API_URL || window.location.origin).replace(/\/+$/, "");
    window.location.href = `${apiUrl}/login/github`;
    return Promise.reject(refreshError);
  }
}

// Response interceptor: handle UNAUTHORIZED errors.
axiosInstance.interceptors.response.use(
  response => {
    if (response.data && response.data.errors) {
      const unauthorizedError = response.data.errors.find(
        (err: any) => err.extensions && err.extensions.code === "UNAUTHORIZED"
      );
      if (unauthorizedError) {
        const originalRequest = response.config as RetryableRequestConfig;
        if (originalRequest && !originalRequest._retry) {
          return handleUnauthorized(originalRequest);
        }
        return Promise.reject(response);
      }
    }
    return response;
  },
  async (error: AxiosError) => {
    if (error.config && error.config.url === "/auth/refresh") {
      return Promise.reject(error);
    }
    const originalRequest = (error.config || (error.response && error.response.config)) as RetryableRequestConfig | undefined;
    const errorData = (error as any).data || (error.response && error.response.data);
    let unauthorized = false;
    if (errorData && errorData.errors) {
      unauthorized = errorData.errors.some(
        (err: any) => err.extensions && err.extensions.code === "UNAUTHORIZED"
      );
    }
    if (!unauthorized && error.response && error.response.status === 401) {
      unauthorized = true;
    }
    if (originalRequest && unauthorized && !originalRequest._retry) {
      return handleUnauthorized(originalRequest);
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
